<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import MortgageLink from 'dashboard/components-next/lais/MortgageLink.vue';
import PaymentInfo from 'dashboard/components-next/lais/PaymentInfo.vue';

defineProps({
  paymentOptions: {
    type: Array,
    required: true,
  },
  customSolicitationTypeOptions: {
    type: Array,
    required: true,
  },
  rentalOwnerReceiveValue: {
    type: Number,
    default: 0,
  },
  formatFormBRL: {
    type: Function,
    required: true,
  },
  paymentNeedsFgts: {
    type: Function,
    required: true,
  },
  isFinancingPayment: {
    type: Function,
    required: true,
  },
  paymentBreakdownHint: {
    type: String,
    default: '',
  },
  paymentBreakdownDifference: {
    type: Number,
    default: 0,
  },
  purchasePaymentRows: {
    type: Array,
    required: true,
  },
  paymentRecalculating: {
    type: Boolean,
    default: false,
  },
  isFinancingProposalPayment: {
    type: Boolean,
    default: false,
  },
  financingInstallmentLabel: {
    type: String,
    default: '',
  },
  accountId: {
    type: [String, Number],
    default: '',
  },
  selectedCatalogImovelId: {
    type: [String, Number],
    default: '',
  },
  showSfhOption: {
    type: Boolean,
    default: false,
  },
  isSfhEligibleForProposal: {
    type: Boolean,
    default: false,
  },
  sfhLimitLabel: {
    type: String,
    default: '',
  },
  schedulePaymentBreakdownDefaults: {
    type: Function,
    required: true,
  },
  syncPaymentBreakdownDefaultsNow: {
    type: Function,
    required: true,
  },
  recalculatePaymentFees: {
    type: Function,
    required: true,
  },
  handleMortgageLinkClick: {
    type: Function,
    required: true,
  },
});

const proposalForm = defineModel('proposalForm', {
  type: Object,
  required: true,
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <section class="grid gap-3">
    <div class="grid gap-3 md:grid-cols-2">
      <div class="flex flex-col gap-1">
        <label
          class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
        >
          Tipo
        </label>
        <select
          v-model="proposalForm.kind"
          class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text focus:border-lais-primary focus:outline-none"
        >
          <option value="offer">Proposta</option>
          <option value="booking">Reserva</option>
        </select>
      </div>
      <div class="flex flex-col gap-1">
        <label
          class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
        >
          Contexto
        </label>
        <select
          v-model="proposalForm.business_context"
          class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text focus:border-lais-primary focus:outline-none"
        >
          <option value="purchase">Venda</option>
          <option value="rental">Aluguel</option>
        </select>
      </div>
      <div class="flex flex-col gap-1">
        <label
          class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
        >
          {{
            proposalForm.business_context === 'purchase'
              ? 'Valor final da venda'
              : 'Aluguel'
          }}
        </label>
        <input
          v-model="proposalForm.offer_amount"
          type="number"
          min="0"
          step="0.01"
          required
          class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text focus:border-lais-primary focus:outline-none"
          @input="schedulePaymentBreakdownDefaults"
          @blur="syncPaymentBreakdownDefaultsNow"
        />
        <p
          v-if="proposalForm.business_context === 'purchase'"
          class="text-[12px] text-lais-text-muted"
        >
          incluindo corretagem
        </p>
      </div>
      <div
        v-if="proposalForm.business_context === 'purchase'"
        class="flex flex-col gap-1"
      >
        <label
          class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
        >
          Forma de pagamento
        </label>
        <select
          v-model="proposalForm.payment_kind"
          class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text focus:border-lais-primary focus:outline-none"
          @change="syncPaymentBreakdownDefaultsNow"
        >
          <option
            v-for="option in paymentOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </div>
      <template v-if="proposalForm.business_context === 'rental'">
        <div class="flex flex-col gap-1">
          <label
            class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
          >
            IPTU
          </label>
          <input
            v-model="proposalForm.rent_iptu_amount"
            type="number"
            min="0"
            step="0.01"
            class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text focus:border-lais-primary focus:outline-none"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label
            class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
          >
            Condomínio
          </label>
          <input
            v-model="proposalForm.rent_condominium_amount"
            type="number"
            min="0"
            step="0.01"
            class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text focus:border-lais-primary focus:outline-none"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label
            class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
          >
            Taxa de adm. (%)
          </label>
          <input
            v-model="proposalForm.admin_fee_percentage"
            type="number"
            min="0"
            step="0.01"
            class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text focus:border-lais-primary focus:outline-none"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label
            class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
          >
            Taxa mínima de adm.
          </label>
          <input
            v-model="proposalForm.minimum_admin_fee"
            type="number"
            min="0"
            step="0.01"
            class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text focus:border-lais-primary focus:outline-none"
          />
        </div>
        <div
          class="rounded-lais-sm bg-lais-primary-50/60 px-3 py-2 text-[13px] font-semibold text-lais-text md:col-span-2"
        >
          Proprietário vai receber:
          {{ formatFormBRL(rentalOwnerReceiveValue) }}
        </div>
        <div class="flex flex-col gap-1">
          <label
            class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
          >
            Moradores
          </label>
          <select
            v-model="proposalForm.residents_count"
            class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text focus:border-lais-primary focus:outline-none"
          >
            <option value="1">1 morador</option>
            <option value="2">2 moradores</option>
            <option value="3">3 moradores</option>
            <option value="4">4 moradores</option>
            <option value="5">5 moradores</option>
            <option value="6">6 ou mais moradores</option>
          </select>
        </div>
        <label
          class="flex items-center gap-2 rounded-lais border border-lais-border bg-white px-3 py-2 text-[13px] font-semibold text-lais-text"
        >
          <input
            v-model="proposalForm.has_pets"
            type="checkbox"
            class="size-4 accent-lais-primary"
          />
          Com animal de estimação
        </label>
        <div class="flex flex-col gap-1 md:col-span-2">
          <label
            class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
          >
            Ajustes no imóvel
          </label>
          <select
            v-model="proposalForm.custom_solicitation_type"
            class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text focus:border-lais-primary focus:outline-none"
          >
            <option
              v-for="option in customSolicitationTypeOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </div>
        <div
          v-if="proposalForm.custom_solicitation_type"
          class="flex flex-col gap-1 md:col-span-2"
        >
          <label
            class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
          >
            Detalhes dos ajustes
          </label>
          <textarea
            v-model="proposalForm.custom_solicitation_description"
            rows="3"
            placeholder="Descreva os itens que o interessado solicitou adicionar, remover, reparar ou modificar"
            class="rounded-lais border border-lais-border bg-white px-3 py-2 text-[14px] text-lais-text placeholder:text-lais-text-muted focus:border-lais-primary focus:outline-none"
          />
        </div>
      </template>
      <div
        v-if="proposalForm.business_context === 'purchase'"
        class="flex flex-col gap-1"
      >
        <label
          class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
        >
          Valor total da corretagem
        </label>
        <input
          v-model="proposalForm.brokerage_fee_value"
          type="number"
          min="0"
          step="0.01"
          class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text focus:border-lais-primary focus:outline-none"
          @input="schedulePaymentBreakdownDefaults"
          @blur="syncPaymentBreakdownDefaultsNow"
        />
        <p class="text-[12px] text-lais-text-muted">
          A corretagem é calculada automaticamente com base no valor final da
          venda.
        </p>
      </div>
      <div class="flex flex-col gap-1">
        <label
          class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
        >
          Prazo para resposta
        </label>
        <input
          v-model="proposalForm.response_deadline_date"
          type="date"
          class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text focus:border-lais-primary focus:outline-none"
        />
        <p class="text-[12px] text-lais-text-muted">
          Se preenchido, a proposta aparecerá como “Responder até hoje”,
          “Responder até amanhã” ou pela data escolhida.
        </p>
      </div>
      <div
        v-if="proposalForm.business_context === 'purchase'"
        class="flex flex-col gap-1"
      >
        <label
          class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
        >
          Sinal
        </label>
        <input
          v-model="proposalForm.earnest_payment_amount"
          type="number"
          min="0"
          step="0.01"
          class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text focus:border-lais-primary focus:outline-none"
        />
      </div>
      <div
        v-if="proposalForm.business_context === 'purchase'"
        class="flex flex-col gap-1"
      >
        <label
          class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
        >
          Entrada
        </label>
        <input
          v-model="proposalForm.entry_amount"
          type="number"
          min="0"
          step="0.01"
          class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text focus:border-lais-primary focus:outline-none"
          @input="schedulePaymentBreakdownDefaults"
          @blur="syncPaymentBreakdownDefaultsNow"
        />
      </div>
      <div
        v-if="
          proposalForm.business_context === 'purchase' &&
          paymentNeedsFgts(proposalForm.payment_kind)
        "
        class="flex flex-col gap-1"
      >
        <label
          class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
        >
          Valor de FGTS
        </label>
        <input
          v-model="proposalForm.fgts_amount"
          type="number"
          min="0"
          step="0.01"
          class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text focus:border-lais-primary focus:outline-none"
          @input="schedulePaymentBreakdownDefaults"
          @blur="syncPaymentBreakdownDefaultsNow"
        />
      </div>
      <div
        v-if="
          proposalForm.business_context === 'purchase' &&
          isFinancingPayment(proposalForm.payment_kind)
        "
        class="flex flex-col gap-1"
      >
        <label
          class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
        >
          Valor financiado
        </label>
        <input
          v-model="proposalForm.financed_amount"
          type="number"
          min="0"
          step="0.01"
          class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text focus:border-lais-primary focus:outline-none"
          @input="schedulePaymentBreakdownDefaults"
          @blur="syncPaymentBreakdownDefaultsNow"
        />
      </div>
      <p
        v-if="paymentBreakdownHint"
        class="rounded-lais-sm px-3 py-2 text-[13px] font-semibold md:col-span-2"
        :class="
          Math.abs(paymentBreakdownDifference) < 1
            ? 'bg-emerald-50 text-emerald-700'
            : 'bg-lais-gold/10 text-lais-text'
        "
      >
        {{ paymentBreakdownHint }}
      </p>

      <div
        v-if="proposalForm.business_context === 'purchase'"
        class="grid gap-3 md:col-span-2"
      >
        <PaymentInfo
          :rows="purchasePaymentRows"
          :recalculating="paymentRecalculating"
          subtitle="Custos estimados de aquisição somados ao valor final da venda."
        />

        <div class="grid gap-3 lg:grid-cols-2">
          <div class="rounded-lais border border-lais-border-soft bg-white p-3">
            <div class="flex items-start justify-between gap-3">
              <div>
                <p class="text-[13px] font-semibold text-lais-text">ITBI</p>
                <p class="mt-1 text-[12px] text-lais-text-muted">
                  Alíquota automática por cidade/UF quando disponível.
                </p>
              </div>
              <label
                class="flex shrink-0 items-center gap-2 text-[12px] font-semibold text-lais-text-muted"
              >
                <input
                  v-model="proposalForm.itbi_overridden"
                  type="checkbox"
                  class="size-4 accent-lais-primary"
                  @change="syncPaymentBreakdownDefaultsNow"
                />
                Manual
              </label>
            </div>
            <input
              v-model="proposalForm.itbi_amount"
              type="number"
              min="0"
              step="0.01"
              :disabled="!proposalForm.itbi_overridden"
              class="mt-3 h-10 w-full rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text disabled:bg-lais-bg-soft disabled:text-lais-text-muted focus:border-lais-primary focus:outline-none"
              @input="schedulePaymentBreakdownDefaults"
              @blur="syncPaymentBreakdownDefaultsNow"
            />
          </div>

          <div class="rounded-lais border border-lais-border-soft bg-white p-3">
            <div class="flex items-start justify-between gap-3">
              <div>
                <p class="text-[13px] font-semibold text-lais-text">
                  Cartório e registro
                </p>
                <p class="mt-1 text-[12px] text-lais-text-muted">
                  Escritura, registro e vistoria por faixa de valor.
                </p>
              </div>
              <label
                class="flex shrink-0 items-center gap-2 text-[12px] font-semibold text-lais-text-muted"
              >
                <input
                  v-model="proposalForm.notary_fee_overridden"
                  type="checkbox"
                  class="size-4 accent-lais-primary"
                  @change="syncPaymentBreakdownDefaultsNow"
                />
                Manual
              </label>
            </div>
            <input
              v-model="proposalForm.notary_fee_value"
              type="number"
              min="0"
              step="0.01"
              :disabled="!proposalForm.notary_fee_overridden"
              class="mt-3 h-10 w-full rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text disabled:bg-lais-bg-soft disabled:text-lais-text-muted focus:border-lais-primary focus:outline-none"
              @input="schedulePaymentBreakdownDefaults"
              @blur="syncPaymentBreakdownDefaultsNow"
            />
          </div>
        </div>

        <LaisPrimaryButton
          size="sm"
          variant="ghost"
          icon="i-lucide-refresh-cw"
          @click="recalculatePaymentFees"
        >
          Recalcular taxas automáticas
        </LaisPrimaryButton>

        <div
          v-if="isFinancingProposalPayment"
          class="rounded-lais border border-lais-border bg-lais-bg-soft p-4"
        >
          <div
            class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
          >
            <div>
              <p class="text-[13px] font-semibold text-lais-text">
                Parcela estimada
              </p>
              <p class="mt-1 text-[20px] font-semibold text-lais-primary">
                {{ financingInstallmentLabel || '-' }}
              </p>
              <p class="mt-1 text-[12px] text-lais-text-muted">
                PRICE · {{ proposalForm.financing_term_months }} meses
              </p>
            </div>
            <MortgageLink
              :account-id="accountId"
              :imovel-id="selectedCatalogImovelId"
              :financed-value="proposalForm.financed_amount"
              :has-sfh="proposalForm.has_sfh"
              :first-purchase="proposalForm.first_purchase"
              @click="handleMortgageLinkClick"
            />
          </div>
        </div>
      </div>

      <label
        v-if="proposalForm.business_context === 'purchase'"
        class="flex items-center gap-2 rounded-lais border border-lais-border bg-white px-3 py-2 text-[13px] font-semibold text-lais-text"
      >
        <input
          v-model="proposalForm.first_purchase"
          type="checkbox"
          class="size-4 accent-lais-primary"
          @change="syncPaymentBreakdownDefaultsNow"
        />
        É primeira compra
      </label>
      <label
        v-if="showSfhOption"
        class="flex items-center gap-2 rounded-lais border border-lais-border bg-white px-3 py-2 text-[13px] font-semibold text-lais-text"
        :class="!isSfhEligibleForProposal ? 'opacity-60' : ''"
      >
        <input
          v-model="proposalForm.has_sfh"
          type="checkbox"
          class="size-4 accent-lais-primary"
          :disabled="!isSfhEligibleForProposal"
          @change="syncPaymentBreakdownDefaultsNow"
        />
        Quer usar SFH
        <span
          v-if="!isSfhEligibleForProposal"
          class="text-[12px] font-normal text-lais-text-muted"
        >
          Teto {{ sfhLimitLabel }}
        </span>
      </label>
      <div class="flex flex-col gap-1 md:col-span-2">
        <label
          class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
        >
          Observações
        </label>
        <textarea
          v-model="proposalForm.notes"
          rows="3"
          placeholder="Condições, prazo de resposta ou observações comerciais"
          class="rounded-lais border border-lais-border bg-white px-3 py-2 text-[14px] text-lais-text placeholder:text-lais-text-muted focus:border-lais-primary focus:outline-none"
        />
      </div>
    </div>
  </section>
</template>
